'use client';

import { ProductLoading } from '@components/layout/ProductLoading';
import { Price } from '@components/price/Price';
import { ProductQuery, ResponsiveImageFragment, SiteLocale } from '@graphql/generated';
import { useAppContext } from '@lib/context/appContext';
import t from '@lib/locales';
import { trackProductView } from '@lib/utils/gtmUtils';
import { ProductAndBundleDataType } from '@model/product';
import { formatPrice } from '@utils/index';
import { bundleToBundleData, productToProductData } from '@utils/product-utils';
import { ReactNode, useEffect, useState } from 'react';
import { subPaths } from '../../../next.config';
import { Loading } from '../layout/Loading';
import { ProductHero } from './ProductHero';
import { ProductInfos } from './ProductInfos';
import { ProductItem } from './ProductItem';
import { ProductTabs } from './ProductTabs';
import { Products } from './Products';

type Props = {
  product: ProductQuery['product'];
  locale: SiteLocale;
  baseUrl: string;
  children: ReactNode;
};

export const Product = ({ product, locale, baseUrl, children }: Props) => {
  const [initGtm, setInitGtm] = useState(false);
  const { client, dispatch } = useAppContext();
  const { name, images, skuCode, slug } = product;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProductAndBundleDataType | null>(null);
  const url = `${baseUrl}/${subPaths.products[locale]}/${slug}`;

  useEffect(() => {
    if (!initGtm && product && data) {
      setInitGtm(true);
      trackProductView(data);
    }
  }, [initGtm, product, client, data]);

  const addProductToCart = async (product: ProductAndBundleDataType, qt = 1) => {
    setLoading(true);
    try {
      const result = await (product.type == 'bundles'
        ? client.addBundleToCart(product, undefined, qt, '')
        : client.addToCart(product, undefined, qt, ''));
      setLoading(false);
      dispatch({ type: 'TOGGLE_FEEDBACK_MODAL', data: result });

      const order = await client.getOrder();
      dispatch({ type: 'SET_ORDER', data: order });

      const cartItems = client.countOrderProductsQuantity(order);
      dispatch({ type: 'SET_NUM_ITEMS', data: cartItems });
    } catch (e) {
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const getData = async () => {
    let image: ResponsiveImageFragment = {
      __typename: 'ResponsiveImage',
      src: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
      srcSet: '',
      width: 1,
      height: 1,
      alt: '',
      title: '',
    };
    if (images.length > 0) {
      image = images[0].responsiveImage;
    }
    if (product.isBundle) {
      const bundle = await client.getBundleFromSku(skuCode);
      if (bundle) {
        const bundleData = bundleToBundleData(skuCode, bundle, image, url, name);
        setData(bundleData);
      }
    } else {
      const product = await client.getProductFromSku(skuCode);
      if (product) {
        const productData = productToProductData(skuCode, product, image, url, name);
        setData(productData);
      }
    }
  };

  useEffect(() => {
    if (client && skuCode) {
      getData();
    }
  }, [client, skuCode]);

  return (
    <>
      {loading && <Loading locale={locale} />}
      <div className="product-hero">
        <ProductHero images={product.images} />
        <div className="product-hero__content">
          <h1 className="product-hero__title">{product.name}</h1>
          <div
            className="product-hero__description"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
          <div className="product-hero__content__wrap">
            {!data && <ProductLoading />}
            {data && (
              <>
                <div className="product-hero__price">
                  <Price
                    {...formatPrice(
                      (data.price?.amount_float || 0) + (data.option?.price_amount_float || 0),
                      data.price?.currency_code,
                      locale
                    )}
                  />
                </div>
                {data.option && <div>{t(locale, 'fees_included')}</div>}

                {!data.available && (
                  <div className="product-hero__notavailable">
                    {t(locale, 'unavailable_message')}
                  </div>
                )}
                {data.available && (
                  <ProductItem locale={locale} product={data} addToCart={addProductToCart} />
                )}
              </>
            )}
          </div>
          <ProductInfos infos={product.infos} />
        </div>
      </div>
      <ProductTabs tabs={product.tabs} />
      {children}
      <Products
        products={product.relatedProducts}
        locale={locale}
        baseUrl={baseUrl}
        isRelated={true}
      />
    </>
  );
};
